/**
 * @file DocumentAside.tsx
 * @description Правая панель действия документа (phase19.24 этап 1, шаг 1;
 * admin-design-system.md §5.2).
 *
 * Одна карточка на все складские документы: заголовок капсом («ДОБАВИТЬ
 * СТРОКУ» / «ОТМЕТИТЬ ПОЗИЦИЮ»), поля через `WmsFieldLabel`, снизу —
 * единственная главная кнопка панели и подпись-причина под ней.
 *
 * До контракта панелей было три с половиной: у приёмки рамка `primary/40`, у
 * списания `destructive/40`, у перемещения снова `primary/40`, а у сборки
 * карточки не было вовсе (замечание пользователя 2026-09-20). Теперь рецепт
 * один — `ring-ghost` на `surface-lowest`, цветом кричит кнопка, а не рамка.
 *
 * `footer` держит кнопку во всю ширину и ≥48px классами обёртки: иначе каждая
 * форма задавала свой размер и панели разъезжались по высоте.
 *
 * `chrome="lg"` — карточка появляется только на `≥lg`: на телефоне панель
 * сборки разворачивается в одиночное поле скана над списком, и рамка вокруг
 * него была бы пустой коробкой.
 *
 * `footerClassName` прячет слот кнопки целиком (`hidden lg:flex`). Без него
 * обёртка футера оставалась в потоке и на телефоне добавляла пустой зазор
 * `gap-3` под полем скана, хотя сама кнопка была скрыта (замечание ревью
 * 2026-09-20).
 */

import type { ReactNode } from "react";

import { SectionLabel } from "./SectionLabel";
import { cn } from "@/lib/utils";

interface Props {
  /** Заголовок панели: «Добавить строку», «Отметить позицию». */
  title: string;
  /** Поля панели. */
  children: ReactNode;
  /** Главная кнопка панели и подпись-причина под ней. */
  footer?: ReactNode;
  /** Классы слота кнопки: `hidden lg:flex` убирает его из потока на телефоне. */
  footerClassName?: string;
  /** `lg` — на телефоне панель без карточки (одно поле скана над списком). */
  chrome?: "always" | "lg";
  /** Видимость заголовка: панель без карточки прячет его на `<lg`. */
  titleClassName?: string;
  className?: string;
}

export function DocumentAside({
  title,
  children,
  footer,
  footerClassName,
  chrome = "always",
  titleClassName,
  className,
}: Props) {
  return (
    <aside
      className={cn(
        "flex min-w-0 flex-col gap-3 lg:sticky lg:top-4 lg:self-start",
        chrome === "always"
          ? "rounded-xl bg-surface-lowest p-4 ring-ghost lg:p-5"
          : "lg:rounded-xl lg:bg-surface-lowest lg:p-5 lg:ring-ghost",
        className,
      )}
    >
      <SectionLabel as="h2" className={titleClassName}>
        {title}
      </SectionLabel>

      <div className="flex flex-col gap-3">{children}</div>

      {footer ? (
        // Кнопка панели — одна, во всю ширину, тач-цель ≥48px: размер задаёт
        // обёртка, чтобы формы не назначали его каждая по-своему.
        <div
          className={cn(
            "flex flex-col gap-1 [&>button]:min-h-12 [&>button]:w-full",
            footerClassName,
          )}
        >
          {footer}
        </div>
      ) : null}
    </aside>
  );
}
